import { Tooltip } from 'antd';
import { NavLink } from 'react-router-dom';
import {
  EmailClickedTooltipIcon,
  EmailOpenedTooltipIcon,
  EmailOpenIcon,
  EmailTooltipIcon,
  FailedEmailIcon,
  SmsTooltipIcon,
  UndeliveredSmsIcon,
  VisibilityOffIcon,
  WarningIcon,
  ClickedIcon,
} from '../../../../src/icons';
import {
  capitalizeFirstLetter,
  getReminderTime,
} from '../../appointments/utils/constants';
import { useState } from 'react';
import {
  CheckCircleFilled,
  ClockCircleFilled,
  PieChartOutlined,
  PlayCircleFilled,
  StopOutlined,
  QuestionCircleFilled,
} from '@ant-design/icons';

const WorkflowStage = ({
  status,
  stageName,
  isApplicable = true,
  isProcessed = false,
}) => {
  const getWorkflowStageStyle = () => {
    if (!isApplicable) {
      return {
        icon: <StopOutlined style={{ fontSize: '24px', color: '#9ca3af' }} />,
        bgColor: 'bg-gray-100',
        borderColor: 'border-gray-300',
        tooltipText: `${stageName} - Aura only`,
      };
    }

    if (isProcessed) {
      return {
        icon: (
          <CheckCircleFilled style={{ fontSize: '24px', color: '#6366f1' }} />
        ),
        bgColor: 'bg-indigo-100',
        borderColor: 'border-indigo-400',
        tooltipText: `${stageName} - Processed`,
      };
    }

    const defaultStyle = {
      icon: (
        <QuestionCircleFilled style={{ fontSize: '24px', color: '#9ca3af' }} />
      ),
      bgColor: 'bg-gray-100',
      borderColor: 'border-gray-300',
      tooltipText: `${stageName} - ${status}`,
    };

    if (stageName === 'Patient Intake') {
      switch (status) {
        case 'New':
          return {
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#3b82f6"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-blue-300"
                style={{ width: '24px', height: '24px' }}
              >
                <circle cx="12" cy="12" r="10"></circle>
              </svg>
            ),
            bgColor: 'bg-gray-100',
            borderColor: 'border-blue-300',
            tooltipText: `${stageName} - Not started`,
          };
        case 'InProgress':
          return {
            icon: (
              <ClockCircleFilled
                style={{ fontSize: '24px', color: '#ffc83d' }}
              />
            ),
            bgColor: 'bg-amber-50',
            borderColor: 'border-amber-200',
            tooltipText: `${stageName} - In progress`,
          };
        case 'partial_completed':
          return {
            icon: (
              <PieChartOutlined
                style={{ fontSize: '24px', color: '#fb923c' }}
              />
            ),
            bgColor: 'bg-orange-100',
            borderColor: 'border-orange-200',
            tooltipText: `${stageName} - Partially completed`,
          };
        case 'Completed':
          return {
            icon: (
              <CheckCircleFilled
                style={{ fontSize: '24px', color: '#10b981' }}
              />
            ),
            bgColor: 'bg-emerald-100',
            borderColor: 'border-emerald-300',
            tooltipText: `${stageName} - Completed`,
          };
        case 'Summary InProgress':
          return {
            icon: (
              <PlayCircleFilled
                style={{ fontSize: '24px', color: '#2cb910' }}
              />
            ),
            bgColor: 'bg-white',
            borderColor: 'border-green-400',
            tooltipText: `${stageName} - Summarizing`,
          };
        default:
          return defaultStyle;
      }
    } else {
      switch (status) {
        case 'new':
          return {
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#3b82f6"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-blue-300"
                style={{ width: '24px', height: '24px' }}
              >
                <circle cx="12" cy="12" r="10"></circle>
              </svg>
            ),
            bgColor: 'bg-gray-100',
            borderColor: 'border-blue-300',
            tooltipText: `${stageName} - Not started`,
          };
        case 'inprogress':
          return {
            icon: (
              <ClockCircleFilled
                style={{ fontSize: '24px', color: '#ffc83d' }}
              />
            ),
            bgColor: 'bg-amber-50',
            borderColor: 'border-amber-200',
            tooltipText: `${stageName} - In progress`,
          };
        case 'completed':
          return {
            icon: (
              <CheckCircleFilled
                style={{ fontSize: '24px', color: '#10b981' }}
              />
            ),
            bgColor: 'bg-emerald-100',
            borderColor: 'border-emerald-300',
            tooltipText: `${stageName} - Completed`,
          };
        default:
          return defaultStyle;
      }
    }
  };

  const [showTooltip, setShowTooltip] = useState(false);
  const { icon, bgColor, borderColor, tooltipText } = getWorkflowStageStyle();

  return (
    <div className="relative inline-block">
      <Tooltip title={showTooltip ? tooltipText : null} placement="top">
        <div
          className={`${bgColor} ${borderColor} border-2 rounded-full w-8 h-8 cursor-pointer relative flex items-center justify-center`}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {icon}
        </div>
      </Tooltip>
    </div>
  );
};

const NotificationDetails = ({ type, status, time }) => (
  <div className="flex mt-1">
    {type === 'email' ? (
      <EmailTooltipIcon className="mr-1" />
    ) : (
      <SmsTooltipIcon className="mr-1 mt-0.5" />
    )}
    {`${type === 'email' ? 'Email' : 'Text'} : `}
    {status}
    {time && <span className="ml-1 tooltip-text">{time}</span>}
  </div>
);

const getNotificationStatusText = (event, type) => {
  if (event === 'exception') return 'Undelivered';
  if (type === 'email' && event === 'delivered')
    return 'Not Seen. Last Delivered';
  if (type === 'sms' && event === 'sent') return 'Last Delivered';
  return capitalizeFirstLetter(event);
};

const getNotificationIcon = (type, event) => {
  if (type === 'email') {
    if (event === 'clicked')
      return <EmailClickedTooltipIcon className="mr-1 mt-1" />;
    if (event === 'opened')
      return <EmailOpenedTooltipIcon className="mr-1 mt-0.5" />;
    if (event === 'delivered') return <EmailTooltipIcon className="mr-1" />;
    if (['failed', 'bounced', 'exception'].includes(event))
      return <FailedEmailIcon className="mr-1 mt-1" />;
  } else {
    if (event === 'delivered')
      return <SmsTooltipIcon className="mr-1 mt-0.5" />;
    if (event === 'exception')
      return <UndeliveredSmsIcon className="mr-1 mt-1" />;
  }
  return null;
};

const NotificationStatus = ({ type, event, time, showTime = true }) => {
  const icon = getNotificationIcon(type, event);
  const statusText = getNotificationStatusText(event, type);

  return (
    <div className={`flex ${type === 'sms' ? 'mt-1' : ''}`}>
      {icon}
      {`${type === 'email' ? 'Email' : 'Text'} : `}
      {statusText}
      {showTime && time && (
        <span className={`ml-${type === 'email' ? '1' : '3'} tooltip-text`}>
          {event === 'exception' ? '' : time}
        </span>
      )}
    </div>
  );
};

const AppointmentStatus = ({ record, hasVCAProcessedWorkflowAccess }) => {
  const responseTime =
    record?.notification_status?.email?.patient_intake_reminder?.time;
  const reminderTime = getReminderTime(responseTime);

  const getNewIntakeNotificationStatus = () => {
    const baseConfig = {
      text: 'Not Started',
      color: 'new-response',
      icon: null,
      tooltip: null,
    };

    // Handle notification states for 'New' status
    if (record?.intakeStatus === 'New') {
      const notificationEvent =
        record?.notification_status?.email?.patient_intake_reminder?.event;
      const smsEvent =
        record?.notification_status?.sms?.patient_intake_reminder?.event;

      // Handle clicked state
      if (notificationEvent === 'clicked') {
        return {
          ...baseConfig,
          icon: <ClickedIcon className="chat-icon ml-2" />,
          tooltip: (
            <div className="flex">
              <EmailClickedTooltipIcon className="mr-1 mt-1" />
              <span>
                Email: Last Clicked{' '}
                <span className="tooltip-text">{reminderTime}</span>
              </span>
            </div>
          ),
        };
      }

      // Handle opened state
      if (notificationEvent === 'opened') {
        return {
          ...baseConfig,
          icon: <EmailOpenIcon className="chat-icon ml-2" />,
          tooltip: (
            <div className="flex">
              <EmailOpenedTooltipIcon className="mr-1 mt-0.5" />
              <span>
                Email: Last Opened{' '}
                <span className="tooltip-text">{reminderTime}</span>
              </span>
            </div>
          ),
        };
      }

      // Handle delivered state (email or SMS)
      if (notificationEvent === 'delivered' || smsEvent === 'delivered') {
        return {
          ...baseConfig,
          icon: <VisibilityOffIcon className="chat-icon ml-3 mt-2" />,
          tooltip: (
            <>
              {record?.notification_status?.email?.patient_intake_reminder && (
                <NotificationStatus
                  type="email"
                  event={
                    record?.notification_status?.email?.patient_intake_reminder
                      ?.event
                  }
                  time={reminderTime}
                />
              )}
              {record?.notification_status?.sms?.patient_intake_reminder && (
                <NotificationStatus
                  type="sms"
                  event={
                    record?.notification_status?.sms?.patient_intake_reminder
                      ?.event
                  }
                  time={reminderTime}
                />
              )}
            </>
          ),
        };
      }

      // Handle failed/bounced/exception states
      if (
        ['failed', 'bounced'].includes(notificationEvent) ||
        smsEvent === 'exception'
      ) {
        return {
          ...baseConfig,
          icon: <WarningIcon className="chat-icon ml-2" />,
          tooltip: (
            <>
              {record?.notification_status?.email?.patient_intake_reminder && (
                <NotificationStatus
                  type="email"
                  event={
                    record?.notification_status?.email?.patient_intake_reminder
                      ?.event
                  }
                  time={reminderTime}
                />
              )}
              {record?.notification_status?.sms?.patient_intake_reminder && (
                <NotificationStatus
                  type="sms"
                  event={
                    record?.notification_status?.sms?.patient_intake_reminder
                      ?.event
                  }
                  time={reminderTime}
                />
              )}
            </>
          ),
        };
      }
    }

    return baseConfig;
  };

  const { text, color, icon, tooltip } = getNewIntakeNotificationStatus();

  return (
    <div className="flex justify-start">
      <div className="flex items-center space-x-2 justify-center ml-18">
        <WorkflowStage
          status={record?.intakeStatus}
          stageName="Patient Intake"
          isApplicable={record?.intakeStatus !== 'Unavailable'}
        />
        <div className="h-px w-2 bg-gray-300"></div>
        <WorkflowStage
          status={record?.encounterPreChartStatus}
          stageName="Pre-Chart"
          isApplicable={true}
        />
        <div className="h-px w-2 bg-gray-300"></div>
        <WorkflowStage
          status={record?.encounterInVisitStatus}
          stageName="In-Visit"
          isApplicable={true}
        />
        {record?.isProcessed && (
          <>
            <div className="h-px w-2 bg-gray-300"></div>
            <WorkflowStage
              status={''}
              stageName="Appointment"
              isApplicable={true}
              isProcessed={true}
            />
          </>
        )}
      </div>
      {icon && tooltip && (
        <Tooltip
          title={tooltip}
          placement="topLeft"
          overlayStyle={{
            position: 'fixed',
          }}
        >
          <div className="flex space-x-2 justify-end ml-3">{icon}</div>
        </Tooltip>
      )}
    </div>
  );
};

export default AppointmentStatus;
